/* ==========================================================================
   alanas-drag-scroll-imp-039 — click-and-drag + endless looping for the
   funnel-3 scroll tracks.

   Any element carrying `data-alanas-drag-scroll` becomes draggable with a
   mouse. Touch and trackpad users already scroll these natively, so the
   gesture is limited to fine pointers and never competes with them.

   Adding `data-alanas-drag-scroll-loop` makes that track endless on every
   pointer type: the row is padded out with clones of its own children and the
   scroll position is teleported by whole set widths whenever it nears either
   seam. Because each set is identical, the jump lands on visually identical
   content, so the row never shows an end — dragged, swiped or wheeled.

   Paired with assets/alanas-drag-scroll-imp-039.css. Self-guarding, so several
   sections can each include the tag without binding twice.
   ========================================================================== */
(function () {
  if (window.__alanasDragScrollImp039) return;
  window.__alanasDragScrollImp039 = true;

  var FINE = window.matchMedia('(hover: hover) and (pointer: fine)');
  /* Movement below this is a click, not a drag — so links stay clickable. */
  var THRESHOLD = 5;
  /* Never clone a row into more children than this. */
  var MAX_CHILDREN = 120;
  /* Below four sets the runways meet in the middle and a teleport could land
     inside the opposite runway, bouncing the row back and forth. */
  var MIN_SETS = 4;

  function init(track) {
    if (track.dataset.alanasDragBound === 'true') return;
    track.dataset.alanasDragBound = 'true';

    var pointerId = null;
    var startX = 0;
    var startScroll = 0;
    var pressed = false;
    var moved = false;

    /* ---------------------------------------------------------------- loop */

    var looping = track.hasAttribute('data-alanas-drag-scroll-loop');
    var originals = looping ? Array.prototype.slice.call(track.children) : [];
    /* Distance from a child to its own clone one set later — 0 when the row
       is not (or cannot be) looped. */
    var setWidth = 0;
    var minX = 0;
    var maxX = 0;
    var lastWidth = track.clientWidth;

    function clearClones() {
      for (var i = track.children.length - 1; i >= 0; i--) {
        var child = track.children[i];
        if (child.dataset && child.dataset.alanasLoopClone === 'true') {
          track.removeChild(child);
        }
      }
    }

    function makeClone(node) {
      var clone = node.cloneNode(true);
      clone.dataset.alanasLoopClone = 'true';
      /* The copies repeat what a screen reader already read once. */
      clone.setAttribute('aria-hidden', 'true');
      Array.prototype.forEach.call(
        clone.querySelectorAll('a, button, input, select, textarea, [tabindex]'),
        function (el) { el.setAttribute('tabindex', '-1'); }
      );
      return clone;
    }

    function buildLoop() {
      if (!looping || originals.length === 0) return;

      clearClones();
      setWidth = 0;

      /* A row that fits has nothing to loop through. */
      if (track.scrollWidth <= track.clientWidth + 1) return;

      var first = originals[0];
      var last = originals[originals.length - 1];
      var oneSet = last.getBoundingClientRect().right - first.getBoundingClientRect().left;
      if (oneSet <= 0) return;

      /* One set is parked off each end as the teleport runway; the rest is
         free scrolling, kept a few viewports wide so an ordinary flick
         lands nowhere near a seam. */
      var sets = 2 + Math.ceil((track.clientWidth * 4) / oneSet);
      if (sets < MIN_SETS) sets = MIN_SETS;
      var maxSets = Math.floor(MAX_CHILDREN / originals.length);
      if (sets > maxSets) sets = maxSets;
      if (sets < MIN_SETS) return;

      var frag = document.createDocumentFragment();
      for (var s = 1; s < sets; s++) {
        for (var i = 0; i < originals.length; i++) {
          frag.appendChild(makeClone(originals[i]));
        }
      }
      track.appendChild(frag);

      /* Measured, not computed: this covers the gap between the sets and any
         sub-pixel rounding in the card widths. */
      setWidth = track.children[originals.length].getBoundingClientRect().left -
        first.getBoundingClientRect().left;
      minX = setWidth;
      maxX = track.scrollWidth - track.clientWidth - setWidth;

      /* A teleport out of one runway has to land clear of the other one. */
      if (!(setWidth > 0) || maxX < setWidth * 2) {
        clearClones();
        setWidth = 0;
        return;
      }

      /* Start one set in, so the row can be dragged backwards straight away. */
      track.scrollLeft = setWidth;
    }

    /* Teleports by whole sets whenever the position drifts into a runway. */
    function wrap() {
      if (!setWidth) return;
      var x = track.scrollLeft;
      var delta = 0;

      if (x < minX) {
        delta = Math.ceil((minX - x) / setWidth) * setWidth;
      } else if (x > maxX) {
        delta = -Math.ceil((x - maxX) / setWidth) * setWidth;
      }
      if (delta === 0) return;

      track.scrollLeft = x + delta;
      /* Re-anchor an in-flight drag to the position it actually landed on. */
      startScroll += track.scrollLeft - x;
    }

    track.addEventListener('scroll', wrap, { passive: true });

    /* ---------------------------------------------------------------- drag */

    function syncDraggable() {
      track.classList.toggle('is-draggable', track.scrollWidth > track.clientWidth + 1);
    }

    track.addEventListener('pointerdown', function (e) {
      if (!FINE.matches || e.pointerType === 'touch' || e.button !== 0) return;
      if (track.scrollWidth <= track.clientWidth + 1) return;
      pointerId = e.pointerId;
      startX = e.clientX;
      startScroll = track.scrollLeft;
      pressed = true;
      /* Reset here rather than on drag end: the click this drag produces
         fires after pointerup, and must still be suppressed. */
      moved = false;
    });

    track.addEventListener('pointermove', function (e) {
      if (!pressed || e.pointerId !== pointerId) return;
      var dx = e.clientX - startX;
      if (!moved) {
        if (Math.abs(dx) < THRESHOLD) return;
        moved = true;
        track.classList.add('is-dragging');
        /* Capture keeps the moves coming once the cursor leaves the track. */
        try { track.setPointerCapture(pointerId); } catch (err) { /* no-op */ }
      }
      e.preventDefault();
      track.scrollLeft = startScroll - dx;
      /* Scroll events are async — wrapping here keeps the drag endless even
         while the pointer is held down. */
      wrap();
    });

    function end(e) {
      if (!pressed || (e && e.pointerId !== pointerId)) return;
      pressed = false;
      if (moved) {
        /* Dropping is-dragging restores scroll-snap, which settles the row
           on the nearest card. */
        track.classList.remove('is-dragging');
        try { track.releasePointerCapture(pointerId); } catch (err) { /* no-op */ }
      }
      pointerId = null;
    }

    track.addEventListener('pointerup', end);
    track.addEventListener('pointercancel', end);
    track.addEventListener('lostpointercapture', end);

    /* A drag that happens to end over a link must not navigate. */
    track.addEventListener('click', function (e) {
      if (!moved) return;
      e.preventDefault();
      e.stopPropagation();
    }, true);

    /* Native image/link dragging would hijack the gesture. */
    track.addEventListener('dragstart', function (e) {
      if (FINE.matches) e.preventDefault();
    });

    /* -------------------------------------------------------------- resize */

    var resizeTimer = null;
    function onResize() {
      /* Mobile browsers fire resize as the URL bar collapses; only a real
         width change can invalidate the clone count. */
      if (track.clientWidth === lastWidth) return;
      lastWidth = track.clientWidth;
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        buildLoop();
        syncDraggable();
      }, 150);
    }

    buildLoop();
    syncDraggable();
    window.addEventListener('resize', onResize);
  }

  function scan(root) {
    var scope = root && root.querySelectorAll ? root : document;
    Array.prototype.forEach.call(
      scope.querySelectorAll('[data-alanas-drag-scroll]'),
      init
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { scan(); });
  } else {
    scan();
  }

  /* Sections re-rendered by the theme editor need binding again. */
  document.addEventListener('shopify:section:load', function (e) { scan(e.target); });
})();
